import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Collapse,
  Container,
  Group,
  Loader,
  Modal,
  NumberInput,
  Select,
  Stack,
  Text,
  Textarea,
  Title,
} from '@mantine/core';
import { IconChevronDown, IconChevronUp, IconInfoCircle } from '@tabler/icons-react';
import { PageContainer } from '../../components/ui/PageContainer';
import {
  MAX_SCORE,
  MIN_SCORE,
  ScoreTarget,
  isValidScore,
  useRemoveScore,
  useScoreTargets,
  useSetScore,
} from '../../hooks/useQuarterlyScoreApi';
import { useQuarterlyReport } from '../../hooks/useQuarterlyApi';
import { NodeListPreview, pruneNodes } from '../../components/essays/NodeListPreview';
import { reportingQuarter } from '../../components/essays/quarterPeriod';
import { useUserStore } from '../../store/userStore';

/**
 * «Не могу оценить» с обязательной причиной. Без причины отказ неотличим от
 * «проигнорировал» — ради этого отличия он и существует.
 */
const AbstainModal: React.FC<{
  opened: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  isPending: boolean;
}> = ({ opened, onClose, onConfirm, isPending }) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  return (
    <Modal opened={opened} onClose={onClose} title={t('quarterlyScoring.abstain')} centered>
      <Stack gap="sm">
        <Textarea
          autosize
          minRows={2}
          maxRows={6}
          maxLength={2000}
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          label={t('quarterlyScoring.abstainReason')}
          placeholder={t('quarterlyScoring.abstainPlaceholder')}
          data-autofocus
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={!reason.trim()}
            loading={isPending}
            onClick={() => onConfirm(reason.trim())}
          >
            {t('quarterlyScoring.abstainConfirm')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

/** Отчёт человека под раскрытием: оценивают, прочитав, а не по фамилии. */
const TargetReport: React.FC<{ reportId: string }> = ({ reportId }) => {
  const { t } = useTranslation();
  const { data, isLoading } = useQuarterlyReport(reportId);

  if (isLoading) {
    return (
      <Center py="md">
        <Loader size="sm" />
      </Center>
    );
  }
  if (!data) {
    return (
      <Text size="sm" c="dimmed">
        {t('quarterly.noReport')}
      </Text>
    );
  }
  return <NodeListPreview nodes={pruneNodes(data.report.items || [])} />;
};

const TargetCard: React.FC<{ target: ScoreTarget; myId: string; isLocked: boolean }> = ({
  target,
  myId,
  isLocked,
}) => {
  const { t } = useTranslation();
  const setScore = useSetScore();
  const removeScore = useRemoveScore();
  const [isOpen, setIsOpen] = useState(false);
  const [isAbstainOpen, setIsAbstainOpen] = useState(false);
  const isAbstained = target.myScore?.isAbstained === true;
  // У отказа в комментарии лежит причина — в поле комментария к оценке ей не место.
  const commentOf = (score: ScoreTarget['myScore']) =>
    score && !score.isAbstained ? (score.comment ?? '') : '';
  const [value, setValue] = useState<number | string>(target.myScore?.value ?? '');
  const [comment, setComment] = useState(commentOf(target.myScore));

  // Поле — состояние компонента, а сервер после снятия оценки присылает пустоту.
  // Без синхронизации в поле осталось бы старое число, и человек думал бы, что
  // оценка на месте.
  const serverValue = target.myScore?.value ?? null;
  const lastServerValue = React.useRef(serverValue);
  React.useEffect(() => {
    if (lastServerValue.current !== serverValue) {
      lastServerValue.current = serverValue;
      setValue(serverValue ?? '');
      setComment(commentOf(target.myScore));
    }
  }, [serverValue, target.myScore]);

  const canScore = !!target.reportId && !isLocked;
  const numeric = typeof value === 'number' ? value : Number(value);

  const save = () => {
    if (!target.reportId || !isValidScore(numeric)) return;
    setScore.mutate({
      reportId: target.reportId,
      value: numeric,
      comment: comment.trim() || undefined,
    });
  };

  return (
    <Card withBorder radius="md" padding="md">
      <Group justify="space-between" wrap="wrap" gap="sm" align="flex-start">
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text fw={600}>{target.name}</Text>
          {target.position && (
            <Text size="xs" c="dimmed">
              {target.position}
            </Text>
          )}
        </div>

        {/* Людей без сданного отчёта не убираем из списка: иначе оценивающий
            решит, что назначение ошибочно, и пойдёт спрашивать. */}
        {!target.reportId ? (
          <Badge variant="light" color="gray">
            {t('quarterlyScoring.noSubmittedReport')}
          </Badge>
        ) : isLocked ? (
          <Badge variant="light" color={target.myScore && !isAbstained ? 'teal' : 'gray'}>
            {isAbstained
              ? t('quarterlyScoring.abstainedMine')
              : target.myScore
                ? t('quarterlyScoring.myScoreValue', { value: target.myScore.value })
                : t('quarterlyScoring.notScored')}
          </Badge>
        ) : (
          <Group gap="xs" wrap="nowrap">
            <NumberInput
              w={110}
              min={MIN_SCORE}
              max={MAX_SCORE}
              step={1}
              allowDecimal={false}
              clampBehavior="strict"
              value={value}
              onChange={setValue}
              placeholder={t('quarterlyScoring.scorePlaceholder')}
              aria-label={t('quarterlyScoring.scoreLabel')}
            />
            <Button
              size="sm"
              onClick={save}
              loading={setScore.isPending}
              disabled={!isValidScore(numeric)}
            >
              {t('common.save')}
            </Button>
            {isAbstained && (
              <Badge variant="light" color="gray">
                {t('quarterlyScoring.abstainedMine')}
              </Badge>
            )}
            {!isAbstained && (
              <Button
                size="sm"
                variant="subtle"
                color="gray"
                onClick={() => setIsAbstainOpen(true)}
              >
                {t('quarterlyScoring.abstain')}
              </Button>
            )}
            {target.myScore && (
              <Button
                size="sm"
                variant="subtle"
                color="red"
                loading={removeScore.isPending}
                onClick={() =>
                  target.reportId &&
                  removeScore.mutate({ reportId: target.reportId, authorId: myId })
                }
              >
                {t('quarterlyScoring.removeScore')}
              </Button>
            )}
          </Group>
        )}
      </Group>

      {isAbstained && (
        <Text size="sm" c="dimmed" mt="sm" style={{ whiteSpace: 'pre-wrap' }}>
          {t('quarterlyScoring.abstainedReason', { reason: target.myScore?.comment })}
        </Text>
      )}

      {target.reportId && (
        <AbstainModal
          opened={isAbstainOpen}
          onClose={() => setIsAbstainOpen(false)}
          isPending={setScore.isPending}
          onConfirm={(reason) =>
            setScore.mutate(
              { reportId: target.reportId!, isAbstained: true, comment: reason },
              { onSuccess: () => setIsAbstainOpen(false) },
            )
          }
        />
      )}

      {canScore && (
        <Textarea
          mt="sm"
          autosize
          minRows={2}
          maxRows={6}
          maxLength={2000}
          value={comment}
          onChange={(e) => setComment(e.currentTarget.value)}
          placeholder={t('quarterlyScoring.commentPlaceholder')}
          label={t('quarterlyScoring.commentLabel')}
        />
      )}

      {/* Назначение открывает отчёт только в окне оценивания: до и после него сервер
          ответит 403, и раскрытие показало бы «отчёта нет». */}
      {target.reportId && !isLocked && (
        <>
          <Button
            mt="sm"
            size="xs"
            variant="subtle"
            leftSection={isOpen ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
            onClick={() => setIsOpen((v) => !v)}
          >
            {isOpen ? t('quarterlyScoring.hideReport') : t('quarterlyScoring.showReport')}
          </Button>
          <Collapse in={isOpen}>
            <Card withBorder radius="md" padding="md" mt="sm">
              <TargetReport reportId={target.reportId} />
            </Card>
          </Collapse>
        </>
      )}
    </Card>
  );
};

/**
 * Экран оценивающего: те, на кого он назначен.
 *
 * Чужих оценок здесь нет ни в каком виде — ни значений, ни «оценили 3 из 5», ни
 * среднего. На этом держится независимость оценивания, поэтому сервер их и не
 * отдаёт.
 */
export const QuarterlyScoring: React.FC = () => {
  const { t, i18n } = useTranslation();
  const me = useUserStore((s) => s.user);
  const [period, setPeriod] = useState(() => reportingQuarter());

  const { data, isLoading } = useScoreTargets(period.year, period.quarter);
  const targets = data?.targets;
  const scoringWindow = data?.window;

  const quarterOptions = useMemo(
    () =>
      [1, 2, 3, 4].map((q) => ({ value: String(q), label: t('quarterly.quarterOption', { q }) })),
    [t],
  );
  const yearOptions = useMemo(() => {
    const y = dayjs().year();
    return Array.from({ length: 5 }, (_, i) => String(y - 3 + i));
  }, []);

  const isWindowOpen = scoringWindow?.state === 'open';
  // Календарная дата без времени читается как местная полночь — тот же день в
  // любом поясе.
  const formatDay = (day: string) =>
    dayjs(day)
      .locale(i18n.language || 'ru')
      .format('D MMMM');

  return (
    <PageContainer>
      <Container size="lg" px={0}>
        <Group justify="space-between" mb="lg" wrap="wrap">
          <Title order={2}>{t('quarterlyScoring.title')}</Title>
          <Group gap="sm">
            <Select
              w={150}
              data={quarterOptions}
              value={String(period.quarter)}
              onChange={(v) => v && setPeriod((p) => ({ ...p, quarter: Number(v) }))}
              allowDeselect={false}
            />
            <Select
              w={110}
              data={yearOptions}
              value={String(period.year)}
              onChange={(v) => v && setPeriod((p) => ({ ...p, year: Number(v) }))}
              allowDeselect={false}
            />
          </Group>
        </Group>

        {scoringWindow && targets && targets.length > 0 && (
          <Alert
            mb="md"
            variant="light"
            color={isWindowOpen ? 'blue' : 'gray'}
            icon={<IconInfoCircle size={16} />}
          >
            {scoringWindow.state === 'open'
              ? t('quarterlyScoring.windowOpen', { date: formatDay(scoringWindow.lastDay) })
              : scoringWindow.state === 'pending'
                ? t('quarterlyScoring.windowPending', { date: formatDay(scoringWindow.firstDay) })
                : t('quarterlyScoring.windowClosed')}
          </Alert>
        )}

        {isLoading ? (
          <Center py="xl">
            <Loader />
          </Center>
        ) : !targets || targets.length === 0 ? (
          <Alert variant="light" color="gray" icon={<IconInfoCircle size={16} />}>
            {t('quarterlyScoring.nobodyAssigned')}
          </Alert>
        ) : (
          <Stack gap="md">
            {targets.map((target) => (
              <TargetCard
                key={target.userId}
                target={target}
                myId={me?._id || ''}
                isLocked={!isWindowOpen}
              />
            ))}
          </Stack>
        )}
      </Container>
    </PageContainer>
  );
};
